import { useMemo, useState } from "react";
import { Button, Modal, TextInput, useToast } from "@a2z/ui";

import { useTranslations } from "@/lib/i18n/intl";
import { keys } from "@/lib/i18n/portal/index";
import type { SellerAuthView } from "@/lib/api/types";
import { useUpdateProfileMutation } from "@/shared/profile/services/useUpdateProfileMutation";

const ps = keys.seller.profileSettings;

type Props = {
  isOpen: boolean;
  onClose: () => void;
  storeSlug: string;
  seller: SellerAuthView | null;
};

export function PersonalInfoModal({ isOpen, onClose, storeSlug, seller }: Props) {
  const t = useTranslations();
  const toast = useToast();
  const { mutateAsync: updateProfile, isPending } = useUpdateProfileMutation();

  // Server-baseline + edit-overrides — no useEffect needed, no stale state.
  const initial = useMemo(
    () => ({
      name: seller?.name ?? "",
      email: seller?.email ?? "",
      phone: seller?.phone ?? "",
    }),
    [seller],
  );
  const [edits, setEdits] = useState<{
    name?: string;
    email?: string;
    phone?: string;
  }>({});
  const name = edits.name ?? initial.name;
  const email = edits.email ?? initial.email;
  const phone = edits.phone ?? initial.phone;
  const set = (k: "name" | "email" | "phone", v: string): void =>
    setEdits((p) => ({ ...p, [k]: v }));

  const dirty =
    name !== initial.name ||
    email !== initial.email ||
    phone !== initial.phone;
  const valid =
    name.trim().length > 0 &&
    phone.trim().length > 0;
  const canSave = dirty && valid && !isPending;

  const handleSave = async (): Promise<void> => {
    if (!canSave) return;
    try {
      await updateProfile({
        storeSlug,
        name: name.trim(),
        email: email.trim() || null,
        phone: phone.trim(),
      });
      setEdits({});
      toast.success(t(keys.seller.profileSettings.personalInfo));
      onClose();
    } catch {
      // Error is surfaced globally via the React Query MutationCache handler.
    }
  };

  // Reset local edits when the modal is closed (so the next open shows the
  // saved server state, not a stale draft).
  const handleClose = (): void => {
    setEdits({});
    onClose();
  };

  return (
    <Modal
      closeLabel={t(keys.common.close)}
      isOpen={isOpen}
      onClose={handleClose}
      size="md"
      title={t(ps.personalInfo)}
      subtitle={t(ps.personalInfoDesc)}
      footerActions={
        <>
          <Button variant="outline" size="md" onClick={handleClose}>
            {t(keys.common.cancel)}
          </Button>
          <Button
            variant="secondary"
            size="md"
            onClick={() => void handleSave()}
            disabled={!canSave}
          >
            {isPending
              ? t(ps.saving)
              : t(keys.seller.storeSettings.saveChanges)}
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-3">
        <Field label={t(ps.displayName)}>
          <TextInput
            name="name"
            value={name}
            onChange={(e) => set("name", e.target.value)}
            placeholder={t(ps.namePlaceholder)}
            autoFocus
          />
        </Field>
        <Field label={t(ps.emailLabel)}>
          <TextInput
            name="email"
            type="email"
            value={email}
            dir="ltr"
            onChange={(e) => set("email", e.target.value)}
          />
        </Field>
        <Field label={t(ps.phone)}>
          <TextInput
            name="phone"
            type="tel"
            inputMode="tel"
            value={phone}
            dir="ltr"
            onChange={(e) => set("phone", e.target.value)}
          />
        </Field>
      </div>
    </Modal>
  );
}

function Field({
  label,
  children,
}: {
  label: string;
  children: React.ReactNode;
}) {
  return (
    <label className="flex flex-col gap-1.5">
      <span className="text-[12px] font-medium text-[color:var(--label-ink)]">
        {label}
      </span>
      {children}
    </label>
  );
}
